import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { formatDayLabel } from '@/lib/dates'
import { useDeleteEmail, useEmailText } from '@/queries/clientEmails'
import type { ClientEmail } from '@/types/clientEmails'
import { DashCard } from './dashboardParts'
import { LoadError } from './LoadError'
import { RequestsTable } from './RequestsTable'

function ReadEmailDialog({ email }: { email: ClientEmail }) {
  const [open, setOpen] = useState(false)
  const text = useEmailText(email._id, open)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Read email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{email.subject ?? 'No subject'}</DialogTitle>
          <DialogDescription>{`${formatDayLabel(email.date)}${email.loggedByName ? `, logged by ${email.loggedByName}` : ''}`}</DialogDescription>
        </DialogHeader>
        {text.isError ? (
          <LoadError title="The email didn't load" onRetry={() => text.refetch()} />
        ) : (
          <pre className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words font-sans text-[13px] text-etn-text">
            {text.data?.data.text ?? (text.isLoading ? 'Loading…' : '')}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  )
}

function DeleteEmailButton({ email }: { email: ClientEmail }) {
  const [open, setOpen] = useState(false)
  const remove = useDeleteEmail()
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">
          Delete email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete email</DialogTitle>
          <DialogDescription>Delete this email and its requests? Tickets already created from it stay.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate({ emailId: email._id }, { onSuccess: () => setOpen(false) })}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function EmailPanel({
  email,
  companyId,
  companyName,
  trackerEnabled,
  highlightRequestId,
}: {
  email: ClientEmail
  companyId: string
  companyName: string
  trackerEnabled: boolean
  highlightRequestId?: string
}) {
  return (
    <DashCard
      testId="email-panel"
      source="email"
      title={`${email.subject ?? 'No subject'}, ${formatDayLabel(email.date)}`}
      right={
        <div className="flex items-center gap-1.5">
          <ReadEmailDialog email={email} />
          <DeleteEmailButton email={email} />
        </div>
      }
    >
      <div className="border-b border-[var(--etn-gray-100)] px-[18px] py-2.5 text-[12.5px] text-etn-text-soft">Logged by {email.loggedByName ?? 'a staff member'}</div>
      <RequestsTable email={email} companyId={companyId} companyName={companyName} trackerEnabled={trackerEnabled} highlightId={highlightRequestId} />
    </DashCard>
  )
}
